import { gql } from '@apollo/client';

export const GET_AVAILABLE_SHIPPING_METHODS = gql`
  query getAvailableShippingMethods($cartId: String!) {
    cart(cart_id: $cartId) {
      id
      shipping_addresses {
        firstname
        lastname
        street
        city
        region {
          code
          region_id
        }
        postcode
        country {
          code
        }
        telephone
        available_shipping_methods {
          carrier_code
          carrier_title
          method_code
          method_title
          amount {
            currency
            value
          }
          price_excl_tax {
            currency
            value
          }
        }
      }
    }
  }
`;

export const GET_AVAILABLE_PAYMENT_METHODS = gql`
  query getAvailablePaymentMethods($cartId: String!) {
    cart(cart_id: $cartId) {
      id
      available_payment_methods {
        code
        title
      }
    }
  }
`;

export const GET_COUNTRIES = gql`
  query getCountries {
    countries {
      id
      two_letter_abbreviation
      three_letter_abbreviation
      full_name_locale
      available_regions {
        id
        code
        name
      }
    }
  }
`;

export const SET_SHIPPING_ADDRESS_AND_EMAIL = gql`
  mutation setShippingAddressAndEmail($cartId: String!, $shippingAddress: CartAddressInput!, $email: String!) {
    setGuestEmailOnCart(input: {
      cart_id: $cartId
      email: $email
    }) {
      cart {
        id
        email
      }
    }
    setShippingAddressesOnCart(input: {
      cart_id: $cartId
      shipping_addresses: [{
        address: $shippingAddress
      }]
    }) {
      cart {
        id
        shipping_addresses {
          firstname
          lastname
          street
          city
          region {
            code
            label
          }
          postcode
          country {
            code
            label
          }
          telephone
          available_shipping_methods {
            carrier_code
            carrier_title
            method_code
            method_title
            amount {
              currency
              value
            }
          }
        }
      }
    }
  }
`;

export const SET_SHIPPING_ADDRESS_FOR_CUSTOMER = gql`
  mutation setShippingAddressForCustomer($cartId: String!, $shippingAddress: CartAddressInput!) {
    setShippingAddressesOnCart(input: {
      cart_id: $cartId
      shipping_addresses: [{
        address: $shippingAddress
      }]
    }) {
      cart {
        id
        shipping_addresses {
          firstname
          lastname
          street
          city
          region {
            code
            label
          }
          postcode
          country {
            code
            label
          }
          telephone
          available_shipping_methods {
            carrier_code
            carrier_title
            method_code
            method_title
            amount {
              currency
              value
            }
          }
        }
      }
    }
  }
`;

export const SET_BILLING_ADDRESS = gql`
  mutation setBillingAddress($cartId: String!, $billingAddress: CartAddressInput!, $sameAsShipping: Boolean!) {
    setBillingAddressOnCart(input: {
      cart_id: $cartId
      billing_address: {
        address: $billingAddress
        same_as_shipping: $sameAsShipping
      }
    }) {
      cart {
        id
        billing_address {
          firstname
          lastname
          street
          city
          region {
            code
            label
          }
          postcode
          country {
            code
            label
          }
          telephone
        }
      }
    }
  }
`;

export const SET_SHIPPING_METHOD = gql`
  mutation setShippingMethodOnCart($cartId: String!, $carrierCode: String!, $methodCode: String!) {
    setShippingMethodsOnCart(input: {
      cart_id: $cartId
      shipping_methods: [{
        carrier_code: $carrierCode
        method_code: $methodCode
      }]
    }) {
      cart {
        id
        shipping_addresses {
          selected_shipping_method {
            carrier_code
            carrier_title
            method_code
            method_title
            amount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
        }
      }
    }
  }
`;

export const SET_PAYMENT_METHOD = gql`
  mutation setPaymentMethodOnCart($cartId: String!, $paymentMethod: PaymentMethodInput!) {
    setPaymentMethodOnCart(input: {
      cart_id: $cartId
      payment_method: $paymentMethod
    }) {
      cart {
        id
        selected_payment_method {
          code
          title
        }
      }
    }
  }
`;

export const GET_TOKENBASE_CHECKOUT_CONFIG = gql`
  query getTokenbaseCheckoutConfig($method: String!) {
    tokenBaseCheckoutConfig(method: $method) {
      method
      useVault
      canSaveCard
      forceSaveCard
      defaultSaveCard
      isCcDetectionEnabled
      logoImage
      requireCcv
      sandbox
      canStoreBin
      availableTypes {
        key
        value
      }
      months {
        key
        value
      }
      years {
        key
        value
      }
      hasVerification
      cvvImageUrl
      apiLoginId
      clientKey
    }
  }
`;

export const CREATE_PAYPAL_EXPRESS_TOKEN = gql`
  mutation createPaypalExpressToken($cartId: String!, $code: String!, $express_button: Boolean!, $urls: PaypalExpressUrlsInput!) {
    createPaypalExpressToken(input: {
      cart_id: $cartId
      code: $code
      express_button: $express_button
      urls: $urls
    }) {
      token
      paypal_urls {
        start
        edit
      }
    }
  }
`;

export const SET_PAYMENT_METHOD_AND_PLACE_ORDER = gql`
  mutation setPaymentMethodAndPlaceOrder($cartId: String!, $paymentMethod: PaymentMethodInput!) {
    setPaymentMethodOnCart(input: {
      cart_id: $cartId
      payment_method: $paymentMethod
    }) {
      cart {
        id
        selected_payment_method {
          code
          title
        }
      }
    }
    placeOrder(input: {
      cart_id: $cartId
    }) {
      order {
        order_number
      }
    }
  }
`;

export const PLACE_ORDER = gql`
  mutation placeOrder($cartId: String!) {
    placeOrder(input: {
      cart_id: $cartId
    }) {
      order {
        order_number
      }
    }
  }
`;

export const GET_AUTHNET_HOSTED_PAYMENT_PARAMS = gql`
  query authnetcimHostedPaymentFormParams($cartId: String!, $guestEmail: String) {
    authnetcimHostedPaymentFormParams(input: {
      cartId: $cartId
      method: authnetcim
      guestEmail: $guestEmail
    }) {
      iframeSessionId
      iframeAction
      iframeParams
    }
  }
`;

export const SYNC_AUTHNET_HOSTED_FORM = gql`
  mutation authnetcimSyncHostedForm($cartId: String!, $iframeSessionId: String!, $guestEmail: String) {
    authnetcimSyncHostedForm(input: {
      cartId: $cartId
      iframeSessionId: $iframeSessionId
      method: authnetcim
      source: checkout
      guestEmail: $guestEmail
    }) {
      card {
        id
        label
        selected
        new
        type
        cc_bin
        cc_last4
      }
    }
  }
`;

